/**
 * Keys — every API credential the platform runs on, one tab (CTO 09-22:
 * "one setting where admin see and put api keys… anthropic one openai one
 * enable disable and deepinfra or firework properly this get its own tab").
 *
 * Two groups:
 *   • Our vendor keys — Anthropic (Claude engine + flagship), OpenAI (Codex
 *     engine + GPT flagship). One key each, enable/disable. Saving a vendor
 *     key auto-syncs that vendor's models so they appear on the Swarm tab
 *     with no separate step.
 *   • THE OPEN LANE — DeepInfra / Fireworks / Ollama / … : the infrastructure
 *     the swarm's open models run on, and an EXCLUSIVE choice (CTO 09-22:
 *     "can only enable one open lane at a time"). The primary interaction is
 *     CONNECT: key → Connect → this host becomes the open lane (the server's
 *     single-active invariant flips the previous one off atomically) AND its
 *     model list syncs in automatically — the admin never needs a separate
 *     sync step to see the models on the Swarm tab.
 *
 * Each card: paste-to-rotate key (blank keeps the stored one), live
 * reachability, and an Advanced disclosure for endpoints/wire/billing. The
 * platform seat switch is NOT here — it has its own Seat tab. Backed by the
 * existing /api/admin/providers CRUD + /health + /api/admin/models sync.
 */

import * as React from "react";
import { Check, ChevronDown, ChevronRight, Plus, RefreshCw, Star, Trash2, X } from "lucide-react";
import { Button, Input, Switch } from "./ui";
import { toast } from "react-toastify";
import { adminFetch } from "./adminFetch";

type WireApi = "chat" | "responses";
interface ProviderRow {
  key: string;
  label: string;
  openaiBaseUrl: string;
  anthropicBaseUrl: string | null;
  codexWireApi: WireApi;
  enabled: boolean;
  isActive: boolean;
  hasKey: boolean;
  kind: "open-host" | "vendor";
  billingMode: "per-token" | "subscription";
  monthlyPriceUsd: number | null;
}
interface ProviderHealth { key: string; reachable: boolean; latencyMs: number | null; checkedAt: string; detail: string }

type Draft = { key: string; label: string; openaiBaseUrl: string; anthropicBaseUrl: string; codexWireApi: WireApi; apiKey: string; kind: "open-host" | "vendor" };
const emptyDraft: Draft = { key: "", label: "", openaiBaseUrl: "", anthropicBaseUrl: "", codexWireApi: "chat", apiKey: "", kind: "open-host" };

const PRESETS: Record<string, { label: string; openaiBaseUrl: string; anthropicBaseUrl: string | null; codexWireApi: WireApi; kind: "open-host" | "vendor" }> = {
  deepinfra: { label: "DeepInfra", openaiBaseUrl: "https://api.deepinfra.com/v1/openai", anthropicBaseUrl: "https://api.deepinfra.com/anthropic", codexWireApi: "chat", kind: "open-host" },
  fireworks: { label: "Fireworks AI", openaiBaseUrl: "https://api.fireworks.ai/inference/v1", anthropicBaseUrl: "https://api.fireworks.ai/inference", codexWireApi: "responses", kind: "open-host" },
  together: { label: "Together AI", openaiBaseUrl: "https://api.together.xyz/v1", anthropicBaseUrl: null, codexWireApi: "chat", kind: "open-host" },
  ollama: { label: "Ollama Cloud", openaiBaseUrl: "https://ollama.com/v1", anthropicBaseUrl: "https://ollama.com", codexWireApi: "responses", kind: "open-host" },
  nvidia: { label: "NVIDIA NIM", openaiBaseUrl: "https://integrate.api.nvidia.com/v1", anthropicBaseUrl: null, codexWireApi: "chat", kind: "open-host" },
  anthropic: { label: "Anthropic (Claude)", openaiBaseUrl: "", anthropicBaseUrl: "https://api.anthropic.com", codexWireApi: "responses", kind: "vendor" },
  openai: { label: "OpenAI (GPT / Codex)", openaiBaseUrl: "https://api.openai.com/v1", anthropicBaseUrl: null, codexWireApi: "responses", kind: "vendor" },
  custom: { label: "", openaiBaseUrl: "", anthropicBaseUrl: null, codexWireApi: "chat", kind: "open-host" },
};

const fieldLabel = "text-[10px] uppercase tracking-wide text-[var(--dash-text-muted)]";
const selectCls = "h-8 rounded-md border border-[var(--dash-border)] bg-transparent px-2 text-xs text-[var(--dash-text)] disabled:opacity-50";

// Module scope on purpose — a component declared inside the parent's render
// gets a new identity every render and drops input focus per keystroke.
function KeyCard({
  p, health, busy, onPatch, onDelete, onConnect, onSaveKey,
}: {
  p: ProviderRow;
  health: ProviderHealth | undefined;
  busy: string | null;
  onPatch: (key: string, patch: Record<string, unknown>, busyKey: string, okMsg?: string) => void;
  onDelete: (p: ProviderRow) => void;
  /** Open hosts only: make this the ONE connected open lane (server flips the
   *  previous off atomically) and auto-sync its models. */
  onConnect: (p: ProviderRow) => void;
  /** Key save with follow-through: vendors auto-sync their models; the
   *  connected open lane re-syncs on rotate. */
  onSaveKey: (p: ProviderRow, key: string) => void;
}) {
  const [keyInput, setKeyInput] = React.useState("");
  const [advanced, setAdvanced] = React.useState(false);
  const [adv, setAdv] = React.useState({
    openaiBaseUrl: p.openaiBaseUrl,
    anthropicBaseUrl: p.anthropicBaseUrl ?? "",
    codexWireApi: p.codexWireApi,
    billingMode: p.billingMode,
    monthlyPriceUsd: p.monthlyPriceUsd,
  });

  const saveKey = () => {
    if (!keyInput.trim()) return;
    onSaveKey(p, keyInput.trim());
    setKeyInput("");
  };

  return (
    <div className={`rounded-xl border bg-[var(--dash-panel-bg)] p-3.5 ${p.kind !== "vendor" && p.isActive ? "border-[rgba(31,143,78,0.35)]" : "border-[var(--dash-border)]"}`}>
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[13.5px] font-semibold text-[var(--dash-text)]">{p.label}</span>
        {/* THE OPEN LANE — exclusive Connect (CTO 09-22): one open host at a
            time; connecting flips the previous off and syncs models in. */}
        {p.kind !== "vendor" && (
          p.isActive && p.enabled ? (
            <>
            <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(31,143,78,0.3)] bg-[rgba(31,143,78,0.08)] px-2 py-0.5 text-[10.5px] font-medium text-[#1f8f4e]" title="The swarm's open models run on this lane">
              <Star className="h-2.5 w-2.5 fill-current" /> Connected — the open lane
            </span>
            <button disabled={busy !== null}
              onClick={() => onPatch(p.key, { enabled: false, isActive: false }, `disable:${p.key}`, `${p.label} disabled. Model selections are saved.`)}
              className="rounded-md border border-[var(--dash-border)] px-2 py-1 text-xs text-[var(--dash-text-muted)] disabled:opacity-50"
              aria-label={`Disable ${p.label}`}>
              Disable
            </button>
            </>
          ) : (
            <button
              onClick={() => onConnect(p)}
              disabled={!p.hasKey || busy !== null}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--dash-border)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--dash-text-muted)] enabled:hover:border-[var(--dash-primary)] enabled:hover:text-[var(--dash-primary)] disabled:opacity-50"
              title={p.hasKey ? "Make this the open lane — the current one disconnects and this host's models sync in" : "Save the key first"}
            >
              <Star className="h-2.5 w-2.5" />
              {busy === `connect:${p.key}` ? "Connecting…" : "Connect"}
            </button>
          )
        )}
        {health && (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${health.reachable ? "border-[rgba(31,143,78,0.3)] bg-[rgba(31,143,78,0.08)] text-[#1f8f4e]" : "border-[rgba(217,45,67,0.3)] bg-[rgba(217,45,67,0.08)] text-[#d92d43]"}`}
            title={`${health.detail} (checked ${new Date(health.checkedAt).toLocaleTimeString()})`}
          >
            {health.reachable ? `reachable${health.latencyMs != null ? ` · ${health.latencyMs}ms` : ""}` : "unreachable — check the key"}
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {/* Vendors keep enable/disable (flagship on/off). Open hosts don't —
              connected-or-not IS the state; two overlapping switches was the
              exact confusion this tab replaces. */}
          {p.kind === "vendor" && (
            <label className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--dash-text-secondary)]">
              <Switch
                checked={p.enabled}
                disabled={busy === `enable:${p.key}`}
                onCheckedChange={() => onPatch(p.key, { enabled: !p.enabled }, `enable:${p.key}`, `${p.label} ${p.enabled ? "disabled" : "enabled"}`)}
                aria-label={`Enable ${p.label}`}
              />
              enabled
            </label>
          )}
          <button onClick={() => onDelete(p)} disabled={busy === `del:${p.key}`} title="Delete" className="text-[var(--dash-text-muted)] hover:text-[#d92d43] disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5">
          <span className={fieldLabel}>API key {p.hasKey ? <span className="text-[#1f8f4e] normal-case">· set <Check className="inline h-2.5 w-2.5" /></span> : "· not set"}</span>
          <Input
            type="password"
            name="provider-api-key"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            value={keyInput}
            placeholder={p.hasKey ? "•••••• (paste to replace)" : "paste key"}
            onChange={(e) => setKeyInput(e.target.value)}
            className="h-8 w-64 font-mono text-xs"
          />
        </label>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!keyInput.trim() || busy === `key:${p.key}`} onClick={saveKey}>
          {busy === `key:${p.key}` ? "Saving…" : "Save key"}
        </Button>
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-medium text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]"
        >
          {advanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Advanced
        </button>
      </div>

      {advanced && (
        <div className="mt-2.5 flex flex-wrap items-end gap-2 border-t border-[var(--dash-border)] pt-2.5">
          {p.kind !== "vendor" && (
            <label className="flex flex-col gap-0.5">
              <span className={fieldLabel}>OpenAI-compatible base</span>
              <Input value={adv.openaiBaseUrl} onChange={(e) => setAdv((s) => ({ ...s, openaiBaseUrl: e.target.value }))} className="h-8 w-64 font-mono text-xs" />
            </label>
          )}
          <label className="flex flex-col gap-0.5">
            <span className={fieldLabel}>Anthropic base</span>
            <Input value={adv.anthropicBaseUrl} placeholder="— none —" onChange={(e) => setAdv((s) => ({ ...s, anthropicBaseUrl: e.target.value }))} className="h-8 w-56 font-mono text-xs" />
          </label>
          {p.kind !== "vendor" && (
            <>
              <label className="flex flex-col gap-0.5">
                <span className={fieldLabel}>Codex wire</span>
                <select value={adv.codexWireApi} onChange={(e) => setAdv((s) => ({ ...s, codexWireApi: e.target.value as WireApi }))} className={selectCls}>
                  <option value="chat">chat</option>
                  <option value="responses">responses</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className={fieldLabel}>Billing</span>
                <select value={adv.billingMode} onChange={(e) => setAdv((s) => ({ ...s, billingMode: e.target.value as "per-token" | "subscription" }))} className={selectCls}>
                  <option value="per-token">per token</option>
                  <option value="subscription" disabled>flat monthly plan (not supported)</option>
                </select>
              </label>
              {adv.billingMode === "subscription" && (
                <label className="flex flex-col gap-0.5">
                  <span className={fieldLabel}>$ / month</span>
                  <Input
                    type="number"
                    value={adv.monthlyPriceUsd ?? ""}
                    onChange={(e) => setAdv((s) => ({ ...s, monthlyPriceUsd: e.target.value === "" ? null : Number(e.target.value) }))}
                    className="h-8 w-24 font-mono text-xs"
                  />
                </label>
              )}
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={busy === `adv:${p.key}`}
            onClick={() => onPatch(p.key, { openaiBaseUrl: adv.openaiBaseUrl, anthropicBaseUrl: adv.anthropicBaseUrl, codexWireApi: adv.codexWireApi, billingMode: adv.billingMode, monthlyPriceUsd: adv.monthlyPriceUsd }, `adv:${p.key}`, "Saved")}
          >
            {busy === `adv:${p.key}` ? "Saving…" : "Save advanced"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function KeysPanel() {
  const [providers, setProviders] = React.useState<ProviderRow[]>([]);
  const [health, setHealth] = React.useState<Record<string, ProviderHealth>>({});
  const [checking, setChecking] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [draftOpen, setDraftOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Draft>(emptyDraft);

  const refreshHealth = React.useCallback(async () => {
    setChecking(true);
    try {
      const res = await adminFetch("/api/admin/providers/health");
      const data = await res.json().catch(() => ({}));
      const rows: ProviderHealth[] = Array.isArray(data?.providers) ? data.providers : [];
      setHealth(Object.fromEntries(rows.map((h) => [h.key, h])));
    } catch { /* pills keep previous state */ } finally {
      setChecking(false);
    }
  }, []);

  const load = React.useCallback(async () => {
    try {
      const res = await adminFetch("/api/admin/providers");
      const d = await res.json().catch(() => ({}));
      setProviders(Array.isArray(d?.providers) ? d.providers : []);
    } catch {
      toast.error("Failed to load keys");
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { void load(); void refreshHealth(); }, [load, refreshHealth]);

  const patch = async (key: string, p: Record<string, unknown>, busyKey: string, okMsg?: string) => {
    setBusy(busyKey);
    try {
      const res = await adminFetch("/api/admin/providers", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, patch: p }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || "save failed");
      if (okMsg) toast.success(okMsg);
      await load();
      if ("apiKey" in p || "enabled" in p) void refreshHealth();
      return true;
    } catch (e: any) {
      toast.error(e?.message || "Couldn't save");
      return false;
    } finally {
      setBusy(null);
    }
  };

  /** Pull a source's live model list (open lane or vendor) — fire-and-report;
   *  a sync failure never undoes the key/connect that triggered it. */
  const syncModels = async (p: ProviderRow) => {
    try {
      const body = p.kind === "vendor"
        ? { action: "sync-vendor", vendor: p.key }
        : { action: "sync-open", provider: p.key };
      const res = await adminFetch("/api/admin/models", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.ok === false) throw new Error(j?.error || `HTTP ${res.status}`);
      toast.success(`${p.label}: ${j.fetched ?? "?"} models synced — choose them in Swarm → Model selection`);
      return true;
    } catch (e: any) {
      toast.error(`${p.label} model sync failed: ${e?.message || e} — retry from the Swarm tab or re-save the key`);
      return false;
    }
  };

  /** Discover models before switching hosts. PATCH validates Fireworks access
   *  again against the stored credential, then swaps the active host atomically. */
  const connect = async (p: ProviderRow) => {
    setBusy(`connect:${p.key}`);
    try {
      if (!(await syncModels(p))) return;
      const res = await adminFetch("/api/admin/providers", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: p.key, patch: { isActive: true } }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || "connect failed");
      toast.success(`${p.label} is now the open lane`);
      await load();
      void refreshHealth();
    } catch (e: any) {
      toast.error(e?.message || "Couldn't connect");
    } finally {
      setBusy(null);
    }
  };

  /** Key save with follow-through: vendor keys auto-sync that vendor's models
   *  (flagship rows appear on Swarm with no separate step); rotating the
   *  CONNECTED open lane's key re-syncs it. A dormant open host just stores
   *  the key — its sync happens on Connect. */
  const saveKeyFor = async (p: ProviderRow, apiKey: string) => {
    const saved = await patch(p.key, { apiKey }, `key:${p.key}`, `${p.label} key saved`);
    if (saved && (p.kind === "vendor" || p.isActive)) void syncModels(p);
  };

  const remove = async (p: ProviderRow) => {
    if (!window.confirm(`Delete "${p.label}"? Its key is removed; the swarm falls back to the next enabled host.`)) return;
    setBusy(`del:${p.key}`);
    try {
      const res = await adminFetch("/api/admin/providers", {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: p.key }),
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success(`Deleted ${p.label}`);
      await load();
    } catch {
      toast.error("Couldn't delete");
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    if (!draft.key.trim() || (!draft.openaiBaseUrl.trim() && draft.kind !== "vendor")) {
      toast.error("Pick a preset (or fill slug + base URL)");
      return;
    }
    setBusy("create");
    try {
      const res = await adminFetch("/api/admin/providers", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...draft, key: draft.key.trim().toLowerCase().replace(/[^a-z0-9-]/g, "") }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || "Create failed");
      toast.success(`Added ${draft.label || draft.key}`);
      setDraftOpen(false);
      setDraft(emptyDraft);
      await load();
      void refreshHealth();
    } catch (e: any) {
      toast.error(e?.message || "Create failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="text-xs text-[var(--dash-text-muted)]">Loading…</p>;

  const vendors = providers.filter((p) => p.kind === "vendor");
  const hosts = providers.filter((p) => p.kind !== "vendor");

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-2">
        <p className="text-[12.5px] text-[var(--dash-text-secondary)]">
          Every credential the platform runs on. Keys are stored encrypted and never shown again — paste to replace.
        </p>
        <Button size="sm" variant="outline" className="ml-auto h-7 shrink-0 text-xs" disabled={checking} onClick={() => void refreshHealth()}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
          {checking ? "Checking…" : "Recheck reachability"}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Our vendor keys — Claude & GPT (flagship)</p>
        {vendors.length === 0 && <p className="text-[11.5px] text-[var(--dash-text-muted)]">No vendor key yet — add Anthropic and OpenAI below.</p>}
        {vendors.map((p) => (
          <KeyCard key={p.key} p={p} health={health[p.key]} busy={busy} onPatch={(k, pt, bk, msg) => void patch(k, pt, bk, msg)} onDelete={(row) => void remove(row)} onConnect={(row) => void connect(row)} onSaveKey={(row, k) => void saveKeyFor(row, k)} />
        ))}
      </div>

      <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] p-3.5 text-xs">
        <p className="font-semibold text-[var(--dash-text)]">Fireworks setup</p>
        <p className="mt-1 text-[var(--dash-text-secondary)]">
          Use a pay-per-token Serverless API key for the live swarm. Add the key, then Connect.
          Catalog access is checked before switching hosts. Open Swarm → Model selection, choose Fireworks AI and select the models you want.
        </p>
        <p className="mt-1 text-[var(--dash-text-muted)]">
          Batch inference is a separate offline workflow for evaluations and document processing; connecting here does not submit batch jobs.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {!providers.some((p) => p.key === "fireworks") && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
              setDraft({ ...emptyDraft, ...PRESETS.fireworks, anthropicBaseUrl: PRESETS.fireworks.anthropicBaseUrl || "", key: "fireworks" });
              setDraftOpen(true);
            }}>Add Fireworks</Button>
          )}
          <a href="https://app.fireworks.ai/settings/users/api-keys" target="_blank" rel="noreferrer" className="underline">Get a Fireworks key</a>
          <a href="https://docs.fireworks.ai/guides/batch-inference" target="_blank" rel="noreferrer" className="underline">Batch guide</a>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">The open lane — one connected at a time</p>
        <p className="-mt-1 text-[11.5px] text-[var(--dash-text-muted)]">
          The swarm&apos;s open models run on one connected host. Save a key, then Connect — models sync before the host switches. Open Swarm → Model selection, choose the provider and select models to make them available to agents.
        </p>
        {hosts.length === 0 && <p className="text-[11.5px] text-[var(--dash-text-muted)]">No host yet — add DeepInfra or Fireworks below to power the swarm&apos;s open models.</p>}
        {hosts.map((p) => (
          <KeyCard key={p.key} p={p} health={health[p.key]} busy={busy} onPatch={(k, pt, bk, msg) => void patch(k, pt, bk, msg)} onDelete={(row) => void remove(row)} onConnect={(row) => void connect(row)} onSaveKey={(row, k) => void saveKeyFor(row, k)} />
        ))}
      </div>

      {!draftOpen ? (
        <div>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setDraftOpen(true); setDraft(emptyDraft); }}>
            <Plus className="mr-1 h-3.5 w-3.5" />Add a key
          </Button>
        </div>
      ) : (
        <div className="space-y-2 rounded-xl border border-dashed border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] p-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">New key</span>
            <select
              className={selectCls}
              value={draft.key in PRESETS ? draft.key : draft.key ? "custom" : ""}
              onChange={(e) => {
                const name = e.target.value;
                if (!name) return;
                const preset = PRESETS[name] || PRESETS.custom;
                setDraft((d) => ({
                  ...d,
                  key: name === "custom" ? d.key : name,
                  label: preset.label,
                  openaiBaseUrl: preset.openaiBaseUrl,
                  anthropicBaseUrl: preset.anthropicBaseUrl || "",
                  codexWireApi: preset.codexWireApi,
                  kind: preset.kind,
                }));
              }}
              aria-label="Preset"
            >
              <option value="">Pick a provider…</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT / Codex)</option>
              <option value="deepinfra">DeepInfra</option>
              <option value="fireworks">Fireworks AI</option>
              <option value="together">Together AI</option>
              <option value="ollama">Ollama Cloud</option>
              <option value="nvidia">NVIDIA NIM</option>
              <option value="custom">Custom…</option>
            </select>
            <button onClick={() => setDraftOpen(false)} className="ml-auto text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]" title="Cancel">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-0.5">
              <span className={fieldLabel}>Slug</span>
              <Input value={draft.key} onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))} placeholder="deepinfra" className="h-8 w-28 font-mono text-xs" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className={fieldLabel}>Label</span>
              <Input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} placeholder="DeepInfra" className="h-8 w-32 text-xs" />
            </label>
            {draft.kind !== "vendor" && (
              <label className="flex flex-col gap-0.5">
                <span className={fieldLabel}>OpenAI-compatible base</span>
                <Input value={draft.openaiBaseUrl} onChange={(e) => setDraft((d) => ({ ...d, openaiBaseUrl: e.target.value }))} placeholder="https://…/v1" className="h-8 w-56 font-mono text-xs" />
              </label>
            )}
            <label className="flex flex-col gap-0.5">
              <span className={fieldLabel}>API key</span>
              <Input type="password" name="provider-api-key" autoComplete="off" data-1p-ignore data-lpignore="true" value={draft.apiKey} onChange={(e) => setDraft((d) => ({ ...d, apiKey: e.target.value }))} placeholder="paste key" className="h-8 w-52 font-mono text-xs" />
            </label>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy === "create"} onClick={() => void create()}>
              <Plus className="mr-1 h-3.5 w-3.5" />{busy === "create" ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
